import React, { useState, useEffect } from "react";
import { useBusiness } from "../contexts/BusinessContext";
import { supabase } from "../lib/supabase";
import { toast } from "sonner";

const WEEK_DAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

const DEFAULT_DAY_HOURS = { enabled: true, open: "09:00", close: "17:00", closed: false };

export default function BusinessHoursForm() {
  const { selectedBusiness, updateBusiness } = useBusiness();
  if (!selectedBusiness) return null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [originalHours, setOriginalHours] = useState(
    selectedBusiness?.business_hours || {
      monday: { enabled: true, open: "09:00", close: "17:00", closed: false },
      tuesday: { enabled: true, open: "09:00", close: "17:00", closed: false },
      wednesday: { enabled: true, open: "09:00", close: "17:00", closed: false },
      thursday: { enabled: true, open: "09:00", close: "17:00", closed: false },
      friday: { enabled: true, open: "09:00", close: "17:00", closed: false },
      saturday: { enabled: true, open: "10:00", close: "15:00", closed: false },
      sunday: { enabled: false, open: "09:00", close: "17:00", closed: true }
    }
  );
  const [businessHours, setBusinessHours] = useState({...originalHours});

  // Track if form has changes
  const hasChanges = React.useMemo(() => {
    return JSON.stringify(businessHours) !== JSON.stringify(originalHours);
  }, [businessHours, originalHours]);

  // Initialize form data on component mount or business change
  useEffect(() => {
    if (selectedBusiness) {
      const hours = selectedBusiness.business_hours || {
        monday: { enabled: true, open: "09:00", close: "17:00", closed: false },
        tuesday: { enabled: true, open: "09:00", close: "17:00", closed: false },
        wednesday: { enabled: true, open: "09:00", close: "17:00", closed: false },
        thursday: { enabled: true, open: "09:00", close: "17:00", closed: false },
        friday: { enabled: true, open: "09:00", close: "17:00", closed: false },
        saturday: { enabled: true, open: "10:00", close: "15:00", closed: false },
        sunday: { enabled: false, open: "09:00", close: "17:00", closed: true }
      };
      setOriginalHours(hours);
      setBusinessHours(hours);
    }
  }, [selectedBusiness]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (!hasChanges) {
      setLoading(false);
      return;
    }

    try {
      if (!supabase) {
        throw new Error("Supabase client not initialized");
      }

      const { error } = await supabase
        .from("businesses")
        .update({
          business_hours: businessHours,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedBusiness.id);

      if (error) throw error;

      setSuccess("Business hours updated successfully");
      await updateBusiness(selectedBusiness.id, { business_hours: businessHours });
      setOriginalHours({...businessHours});
      toast.success("Business hours updated successfully");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update business hours"
      );
      toast.error("Failed to update business hours");
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setBusinessHours({...originalHours});
    setError(null);
    setSuccess(null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {WEEK_DAY_ORDER.map((day) => {
          const hours = businessHours[day] || DEFAULT_DAY_HOURS;
          return (
            <div key={day} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setBusinessHours((prev) => ({
                      ...prev,
                      [day]: {
                        ...(prev[day] || DEFAULT_DAY_HOURS),
                        enabled: !(prev[day]?.enabled ?? true),
                        closed: !(prev[day]?.enabled ?? true)
                      }
                    }));
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                    hours.enabled ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      hours.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className="text-sm font-medium text-gray-900 capitalize min-w-[80px]">
                  {day}
                </span>
              </div>
              
              {hours.enabled ? (
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={hours.open}
                    onChange={(e) => {
                      setBusinessHours((prev) => ({
                        ...prev,
                        [day]: {
                          ...(prev[day] || DEFAULT_DAY_HOURS),
                          open: e.target.value
                        }
                      }));
                    }}
                    className="w-20 h-8 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-xs transition-all"
                  />
                  <span className="text-gray-400 text-xs">to</span>
                  <input
                    type="time"
                    value={hours.close}
                    onChange={(e) => {
                      setBusinessHours((prev) => ({
                        ...prev,
                        [day]: {
                          ...(prev[day] || DEFAULT_DAY_HOURS),
                          close: e.target.value
                        }
                      }));
                    }}
                    className="w-20 h-8 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-xs transition-all"
                  />
                </div>
              ) : (
                <span className="text-gray-400 text-sm">Closed</span>
              )}
            </div>
          );
        })}
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-3 py-2 rounded-lg text-sm">
          {success}
        </div>
      )}

      {hasChanges && (
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200/60">
          <button
            type="button"
            onClick={resetForm}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      )}
    </form>
  );
}


